import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Image,
  Modal,
  Row,
  Spinner,
  Stack,
} from 'react-bootstrap';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faFile,
  faFileExcel,
  faFilePdf,
  faFileWord,
} from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios, { isAxiosError } from 'axios';
import { groupBy } from 'lodash';
import Sentry from 'src/services/sentry';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { About, MDRegion } from 'src/models/AboutTab.model';
import { Api } from 'src/models/Api.model';
import { Download, whichDownload } from 'src/models/Downloads.model';
import { DeviceDataValue } from 'src/models/GalenData/DeviceDataValue.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import { getLocaleCode, Language } from 'src/models/Language.model';
import { ProductFullName } from 'src/models/Product.model';
import { isEU, Region, whichRegion } from 'src/models/Region.model';

import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetGalenErrorMsg from 'src/hooks/useGetGalenErrorMsg';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

function imgSrc(type: string | undefined) {
  return new URL(`/src/assets/img/AboutTab/${type}.png`, import.meta.url).href;
}

const AboutTab = () => {
  const [showOss, setShowOss] = useState(false);
  const [ossText, setOssText] = useState('');
  const [pageData, setPageData] = useState<PageData>();
  const [manuals, setManuals] = useState<DeviceDataValue<string>[]>([]);

  // TODO: to be uncommented when TOS is implemented
  // const [termsOfServices, setTermsOfServices] = useState<
  //   DeviceDataValue<string>[]
  // >([]);

  const [otherFiles, setOtherFiles] = useState<DeviceDataValue<string>[]>([]);
  const [mdRegion, setMdRegion] = useState<MDRegion>('EU');

  const handleCloseOss = () => setShowOss(false);
  const handleShowOss = () => {
    const OSS_URL = '/THIRD-PARTY-NOTICES.txt';
    if (ossText === '') {
      axios
        .get(OSS_URL)
        .then((res) => {
          setOssText(res.data);
          setShowOss(true);
        })
        .catch((err) => {
          setOssText(`Error: ${err.message}`);
          setShowOss(true);
        });
    } else {
      setShowOss(true);
    }
  };

  interface IfileIcons {
    pdf: IconProp;
    doc: IconProp;
    xls: IconProp;
    [key: string]: IconProp;
  }

  const fileIcons: IfileIcons = {
    pdf: faFilePdf,
    doc: faFileWord,
    xls: faFileExcel,
  };

  interface PageData {
    ManufacturerText?: DeviceDataValue<string>;
    Email?: DeviceDataValue<string>;
    Name?: DeviceDataValue<string>;
    PhoneNumber?: DeviceDataValue<string>;
  }

  const DownloadItem = (download: DeviceDataValue<string>) => (
    <li>
      <FontAwesomeIcon
        icon={fileIcons[download?.value.split('.').pop() || ''] || faFile}
        className="me-3 text-dark"
      />
      <a href={download?.link} rel="noreferrer" className="text-dark">
        {download?.value}
      </a>
    </li>
  );

  const [isLoading, setIsLoading] = useState(false);

  const {
    practiceSettings: { product },
  } = useRemoteBoundStore();

  const { practiceId, userEmail } = useGetCurrentUser();

  const { tPages, tCommon, language } = useUiTranslation();
  const { getErrorMessage } = useGetGalenErrorMsg();
  const { handleHttpRequestError } = useHandleHttpRequestError();

  useEffect(() => {
    const fetchText = async () => {
      try {
        const data = await Api.remote._get_all_device_data({
          deviceDataModelId: import.meta.env.VITE_ABOUT_PAGE_DATA_MODEL,
          pageNumber: 0,
          pageSize: 1,
        });

        const deviceDataList = data.data.content || [];
        return deviceDataList[0]['data'];
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          Sentry.captureMessage(
            `Missing contact support data in About page for user ${userEmail} and practice ${practiceId}`,
          );

          return Api.alertBox({
            title: tCommon('bootbox.title.Error'),
            message: tPages('about.missingContactSupport'),
            locale: getLocaleCode(language as Language),
            type: 'error',
          });
        }
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: errorMessage,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
      }
    };

    const whichMDRegion = (country: string): MDRegion => {
      if (isEU(country)) return Region.EU;

      switch (country) {
        case Region.US: {
          return Region.US;
        }
        case Region.AU: {
          return Region.AU;
        }
        case Region.ID: {
          return Region.ID;
        }
        case Region.PH: {
          return Region.PH;
        }
        case Region.VN: {
          return Region.VN;
        }
        case Region.AE: {
          return Region.AE;
        }
        default: {
          return Region.EU;
        }
      }
    };

    const fetchRegion = async () => {
      try {
        if (!practiceId) {
          throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_IS_REQUIRED));
        }

        const practice = await Api.remote.get_practice(practiceId);
        const country = practice.data.contactInfo.country;

        if (country !== undefined) {
          setMdRegion(whichMDRegion(country));
          return whichRegion(country);
        }
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        if (errorMessage) {
          Api.alertBox({
            title: tCommon('bootbox.title.Error'),
            message: errorMessage,
            locale: getLocaleCode(language as Language),
            type: 'error',
          });
        }
      }
    };

    const fetchFiles = async () => {
      try {
        const data = await Api.remote._get_all_device_data({
          deviceDataModelId: import.meta.env
            .VITE_ABOUT_PAGE_DOWNLOAD_DATA_MODEL,
        });
        const deviceDataList = data.data.content || [];
        return deviceDataList.map((item) => item.data.File);
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          return [];
        }

        const errorMessage = handleHttpRequestError(error);
        if (errorMessage) {
          Api.alertBox({
            title: tCommon('bootbox.title.Error'),
            message: errorMessage,
            locale: getLocaleCode(language as Language),
            type: 'error',
          });
        }
      }
    };

    const processFilename = (item: DeviceDataValue<string>) => {
      const fileSplit = item.value.split(' ');
      const fileType = whichDownload(fileSplit[0]);
      const fileRegion = whichRegion(fileSplit[1]);

      return { ...item, fileType, fileRegion };
    };

    const fetchPage = async () => {
      try {
        const textData = await fetchText();
        if (textData !== undefined) {
          setPageData(textData);
        }

        const regionData = await fetchRegion();

        if (regionData !== undefined) {
          const filesData = await fetchFiles();
          if (filesData !== undefined) {
            const processedFileData = filesData.map(processFilename);

            const regionList = processedFileData.filter(
              (item) => item.fileRegion === regionData,
            );

            const groupedFiles = groupBy(regionList, 'fileType');

            const tempManuals = groupedFiles[Download.userManual] || [];

            // TODO: to be uncommented when TOS is implemented
            // const tempTermsOfServices =
            //   groupedFiles[Download.termsOfService] || [];

            const tempOtherFiles = groupedFiles[Download.other] || [];

            setManuals(tempManuals);

            // TODO: to be uncommented when TOS is implemented
            // setTermsOfServices(tempTermsOfServices);
            setOtherFiles(tempOtherFiles);
          }
        }
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        if (errorMessage) {
          Api.alertBox({
            title: tCommon('bootbox.title.Error'),
            message: errorMessage,
            locale: getLocaleCode(language as Language),
            type: 'error',
          });
        }
      }
    };

    setIsLoading(true);
    fetchPage().then(() => setIsLoading(false));
  }, [
    getErrorMessage,
    handleHttpRequestError,
    practiceId,
    tPages,
    tCommon,
    userEmail,
    language,
  ]);

  return (
    <>
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center h-100">
          <Spinner className="mx-5 my-5" variant="primary" />
        </div>
      ) : (
        <div className="overflow-y-auto overflow-x-hidden">
          <Row>
            <Col>
              <h3>{tPages('about.title')}</h3>
            </Col>
          </Row>

          <Row>
            {!['ID', 'PH', 'VN'].includes(mdRegion) && (
              <Col md={6}>
                <Card className="data-container mt-2 p-0">
                  <Card.Body>
                    <Stack gap={2}>
                      <Stack direction="horizontal" gap={3}>
                        <Image
                          src={imgSrc(About.MdSymbol)}
                          alt={tPages('about.mdSymbolImgAlt')}
                          fluid
                          style={{ width: 60 }}
                        />
                        <h4 className="fw-semibold pt-1">
                          {About[mdRegion].MDName[product]}
                        </h4>
                      </Stack>
                      {About[mdRegion].CeMarkSymbol && (
                        <Stack direction="horizontal" gap={3}>
                          <Image
                            src={imgSrc(About[mdRegion].CeMarkSymbol)}
                            alt={tPages('about.ceMark0297SymbolImgAlt')}
                            fluid
                            style={{ width: 70 }}
                          />
                          <div className="d-flex flex-column">
                            <span>{tPages('about.ceMark')}</span>
                            <span>
                              {`${tPages('about.ceMarkNotifiedBody')} ${About[mdRegion].CeMarkNotifiedBodyName}`}
                            </span>
                          </div>
                        </Stack>
                      )}
                      <Stack direction="horizontal" gap={3}>
                        <Image
                          src={imgSrc(About.ManufacturerSymbol)}
                          alt={tPages('about.manufacturerSymbolImgAlt')}
                          fluid
                          style={{ width: 50 }}
                        />

                        <div className="d-flex flex-column">
                          <span className="fw-semibold">
                            {tPages('about.manufacturerName', {
                              company: About.ManufacturerName,
                              parent: About.ManufacturerParentName,
                            })}
                          </span>
                          <span>
                            {`${tPages('about.manufacturerBuilding')}, ${About.ManufacturerAddress}, ${tPages('about.manufacturerCounty')}`}
                          </span>
                        </div>
                      </Stack>
                      {About[mdRegion].HasEuRep && (
                        <Stack
                          direction="horizontal"
                          gap={3}
                          className="align-items-start"
                        >
                          <div className="d-flex flex-column align-items-center">
                            <Image
                              src={imgSrc(About.EuRepSymbol)}
                              alt={tPages('about.euRepSymbolImgAlt')}
                              fluid
                              style={{ width: 80 }}
                            />
                            <Image
                              src={imgSrc(About.ImporterSymbol)}
                              alt=""
                              fluid
                              style={{ width: 50 }}
                              className="mt-2"
                            />
                          </div>
                          <div className="d-flex flex-column">
                            <span>{About.EuRepName}</span>
                            <span>{About.EuRepAddress}</span>
                            <span>{tPages('about.euRepCityCountry')}</span>
                            <span>{About.EuRepEmail}</span>
                          </div>
                        </Stack>
                      )}
                      {About[mdRegion].HasChRep && (
                        <Stack
                          direction="horizontal"
                          gap={3}
                          className="align-items-start"
                        >
                          <Image
                            src={imgSrc(About.ChRepSymbol)}
                            alt={tPages('about.chRepSymbolImgAlt')}
                            fluid
                            style={{ width: 80 }}
                          />
                          <div className="d-flex flex-column">
                            <span>{About.ChRepName}</span>
                            <span>{About.ChRepAddress}</span>
                            <span>{About.ChRepLocation}</span>
                            <span>{About.ChRepEmail}</span>
                          </div>
                        </Stack>
                      )}
                      {About[mdRegion].RefSymbol && (
                        <Stack direction="horizontal" gap={3}>
                          <Image
                            src={imgSrc(About[mdRegion].RefSymbol)}
                            alt={tPages('about.refSymbolImgAlt')}
                            fluid
                            style={{ width: 45 }}
                          />
                          {`${About[mdRegion].MDName[product]} ${About.ProductVersion[About[mdRegion].ProductVersionType]}`}
                        </Stack>
                      )}
                      <Stack direction="horizontal" gap={3}>
                        <Image
                          src={imgSrc(About.DateofManufactureSymbol)}
                          alt={tPages('about.dateofManufactureSymbolImgAlt')}
                          fluid
                          style={{ width: 50 }}
                        />
                        {About.ManufactureDate}
                      </Stack>
                      {About[mdRegion].UdiNumber && (
                        <Stack direction="horizontal" gap={3}>
                          <Image
                            src={imgSrc(About.UdiSymbol)}
                            alt={tPages('about.udiSymbolImgAlt')}
                            fluid
                            style={{ width: 45 }}
                          />
                          <Image
                            src={imgSrc(About[mdRegion].UdiQrCode)}
                            alt=""
                            fluid
                            style={{ width: 90 }}
                          />
                          <div className="d-flex flex-column">
                            <span>
                              {`${About[mdRegion].UdiNumber}${About.ProductVersion[About[mdRegion].ProductVersionType]}`}
                            </span>
                            <span>
                              {`${About[mdRegion].MDName[product]} ${About.ProductVersion[About[mdRegion].ProductVersionType]}`}
                            </span>
                          </div>
                        </Stack>
                      )}
                      {About[mdRegion].ConsultManualBeforeUseSymbol && (
                        <Stack direction="horizontal" gap={3}>
                          <Image
                            src={imgSrc(
                              About[mdRegion].ConsultManualBeforeUseSymbol,
                            )}
                            alt=""
                            fluid
                            style={{ width: 40 }}
                          />
                          {tPages('about.consultManualBeforeUse')}
                        </Stack>
                      )}
                    </Stack>
                  </Card.Body>
                </Card>
              </Col>
            )}

            <Col md={6}>
              <Card className="data-container mt-2 p-0">
                <Card.Body>
                  <h5>{tPages('about.productVersion')}</h5>
                  <p className="pb-1">{`${About[mdRegion].MDName[product]} ${About.ProductVersion[About[mdRegion].ProductVersionType]} ${About[mdRegion].Mdd ?? ''}`}</p>

                  <h5 className="pt-3 border-top">
                    {tPages('about.contactSupport')}
                  </h5>
                  <ul className="list-unstyled">
                    <li>{pageData?.Name?.value}</li>
                    <li>
                      <a
                        href={`mailto:${pageData?.Email?.value}`}
                        rel="noreferrer"
                        className="text-dark"
                      >
                        {pageData?.Email?.value}
                      </a>
                    </li>
                    <li>{pageData?.PhoneNumber?.value}</li>
                  </ul>
                  {pageData?.ManufacturerText?.value && (
                    <p className="pt-3 pb-1 border-top">
                      {pageData?.ManufacturerText?.value || null}
                    </p>
                  )}
                  {
                    <Stack gap={3}>
                      <div className="pt-3 border-top d-flex align-items-center">
                        <Image
                          src={imgSrc(About.UserManualSymbol)}
                          alt=""
                          fluid
                          className="me-3"
                          style={{ height: 40 }}
                        />
                        <h5 className="pt-2">{tPages('about.userManuals')}</h5>
                      </div>

                      <ul className="list-unstyled">
                        {manuals.map((manual) => (
                          <DownloadItem key={manual.reference} {...manual} />
                        ))}
                      </ul>
                    </Stack>
                  }

                  {/* TODO: to be uncommented when TOS is implemented */}
                  {/* <h3 className="pt-3 border-top">
                    {t('about.termsOfService')}
                  </h3>
                  <ul className="list-unstyled">
                    {termsOfServices.map((termsOfService) => (
                      <DownloadItem
                        key={termsOfService.reference}
                        {...termsOfService}
                      />
                    ))}
                  </ul> */}

                  {otherFiles && (
                    <Stack gap={3}>
                      <h5 className="pt-3 border-top">
                        {tPages('about.otherDownloads')}
                      </h5>
                      <ul className="list-unstyled">
                        {otherFiles.map((otherFile) => (
                          <DownloadItem
                            key={otherFile.reference}
                            {...otherFile}
                          />
                        ))}
                      </ul>
                    </Stack>
                  )}
                  <p className="pt-3 border-top">
                    {tPages('about.productRegistered', {
                      product: ProductFullName(product),
                    })}
                  </p>
                  <p>
                    <strong>
                      &copy;
                      {tPages('about.copyright', {
                        year: new Date().getFullYear(),
                      })}
                    </strong>

                    <a
                      href="#/about"
                      className="ms-2 text-black"
                      onClick={handleShowOss}
                    >
                      {tPages('about.thirdPartyNotices')}
                    </a>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <CustomModal
            show={showOss}
            onHide={handleCloseOss}
            centered
            size="xl"
            scrollable={true}
            fullscreen="xl-down"
          >
            <Modal.Header closeButton>
              <Modal.Title>{tPages('about.thirdPartyNotices')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
              <pre className="m-0 p-3" style={{ maxHeight: '75vh' }}>
                {ossText}
              </pre>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={handleCloseOss}>
                {tPages('about.close')}
              </Button>
            </Modal.Footer>
          </CustomModal>
        </div>
      )}
    </>
  );
};

export default AboutTab;
